import {
    AccountCreationBatchOutcomeEnum,
    BatchAccountActivationResultInterface,
    BatchCreateAccountResultInterface,
    TradingAccountInterface
} from '@spa/api';

export interface BatchAccountCreationRow {
    sequence: number;
    accountReferenceId: string;
    outcome: AccountCreationBatchOutcomeEnum;
    reason: string;
}

export interface BatchAccountCreationState {
    total: number;
    inProgress: boolean;
    succeeded: boolean;
    rows: BatchAccountCreationRow[];
    summaryText: string;
}

export function buildBatchInProgressState(total: number): BatchAccountCreationState {
    return {
        total,
        inProgress: true,
        succeeded: false,
        rows: [],
        summaryText: `Creating ${total} accounts... This may take a moment, please keep this window open.`
    };
}

export function buildBatchFailedState(total: number, message: string): BatchAccountCreationState {
    return {total, inProgress: false, succeeded: false, rows: [], summaryText: message};
}

/**
 * Creation is all-or-nothing, so every created account is listed; activation runs per account
 * afterwards and a missing activation entry is treated as not initiated.
 */
export function buildBatchSucceededState(
    total: number,
    result: BatchCreateAccountResultInterface
): BatchAccountCreationState {
    const createdAccounts = (result && result.created_accounts) || [];
    const activations = (result && result.activations) || [];

    const rows = createdAccounts.map((account: TradingAccountInterface, index: number): BatchAccountCreationRow => {
        const activation = activations.find((item: BatchAccountActivationResultInterface) =>
            item.account_reference_id === account.account_reference_id);

        return {
            sequence: index + 1,
            accountReferenceId: account.account_reference_id,
            outcome: activation ? activation.outcome : AccountCreationBatchOutcomeEnum.CreatedActivationNotInitiated,
            reason: activation ? activation.reason : null
        };
    });

    // Created-Activated means activation was initiated; the orchestrator completes it to Active asynchronously.
    const initiatedCount = rows.filter((row: BatchAccountCreationRow) =>
        row.outcome === AccountCreationBatchOutcomeEnum.CreatedActivated).length;
    const followUpCount = rows.length - initiatedCount;

    let summaryText = `${rows.length} of ${total} accounts created, activation initiated for ${initiatedCount}.`;
    if (followUpCount > 0) {
        summaryText += ` ${followUpCount} need manual follow-up.`;
    }

    return {total, inProgress: false, succeeded: true, rows, summaryText};
}
